import { DateTime } from "luxon";
import { Table } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { BalanceChip } from "../components/balanceChip";
import { GGPlayer, GGRedemptionHistory } from "../types";

import { useGetGooseGameItemLazyQuery } from "./graphql/getGooseGameItem.generated";
import { useGetPurchaseTransactionsQuery } from "./graphql/getPurchaseTransactions.generated";

interface RedemptionHistoryProps {
  GooseGamePlayer: GGPlayer;
  refreshHistory: boolean;
  setRefreshHistory: (refreshHistory: boolean) => void;
}
const RedemptionHistory: React.FC<RedemptionHistoryProps> = ({
  GooseGamePlayer,
  refreshHistory,
  setRefreshHistory,
}) => {
  const {
    data: transactionData,
    loading: transactionLoading,
    refetch,
  } = useGetPurchaseTransactionsQuery({
    variables: { event_attendee_id: GooseGamePlayer.event_attendee_id },
  });

  const [getItem, { data: storeItemData, loading: storeItemLoading }] =
    useGetGooseGameItemLazyQuery();
  const [itemsData, setItemsData] = useState<Record<number, any>>({});
  const [redemptionHistory, setRedemptionHistory] = useState<
    GGRedemptionHistory[]
  >([]);

  useEffect(() => {
    if (!transactionData) return;

    transactionData.gooseGameTransactions
      .filter((t) => t.item_id)
      .forEach((t) => {
        if (!itemsData[t.item_id!]) {
          getItem({ variables: { id: t.item_id ?? 0 } });
        }
      });
  }, [transactionData, getItem, itemsData]);

  useEffect(() => {
    if (!storeItemData?.gooseGameStoreItem) return;
    const id = storeItemData.gooseGameStoreItem.id;
    setItemsData((prev) => ({
      ...prev,
      [id]: storeItemData.gooseGameStoreItem,
    }));
  }, [storeItemData]);

  useEffect(() => {
    if (
      transactionData?.gooseGameTransactions &&
      !storeItemLoading &&
      !transactionLoading
    ) {
      const history: GGRedemptionHistory[] =
        transactionData.gooseGameTransactions.map((transaction) => {
          const item = itemsData[transaction.item_id ?? 0];
          return {
            name: item?.name ?? "",
            pointCost: item?.point_cost ?? 0,
            quantity: transaction.item_quantity ?? 0,
            createdAt: transaction.created_at,
          };
        });
      setRedemptionHistory(history);
    }
  }, [transactionData, itemsData, storeItemLoading, transactionLoading]);

  useEffect(() => {
    if (refreshHistory) refetch();
    setRefreshHistory(false);
  }, [refreshHistory, refetch, setRefreshHistory]);

  const tableColumns = [
    {
      id: "name",
      name: "Name",
    },
    {
      id: "quantity",
      name: "QTY",
    },
    {
      id: "price",
      name: "Price",
    },
    {
      id: "dateRedeemed",
      name: "Date Redeemed",
    },
  ];

  const tableData = redemptionHistory
    .slice()
    .reverse()
    .map((transaction) => {
      const formattedDate = DateTime.fromISO(
        transaction.createdAt.toString()
      ).toFormat("MMM d 'at' h:mm a");
      return {
        name: transaction.name,
        quantity: `x${transaction.quantity}`,
        dateRedeemed: formattedDate,
        price: <BalanceChip points={transaction.pointCost} />,
      };
    });

  return (
    <Container>
      <Table
        displayStyle={"plain"}
        columns={tableColumns}
        data={tableData}
        horizontalPadding={16}
        verticalPadding={10}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 36px;
  max-width: 534px;
`;

export default RedemptionHistory;
